import React, { useMemo } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { fmtMoneyCompact, fmtMoney } from '../utils/format';

/**
 * Bridge chart that walks from Period B Net Profit → Period A Net Profit,
 * showing the contribution of each P&L driver.
 *
 * Drivers (signed so that "+" improves profit):
 *   + Sales change                = A.sales - B.sales
 *   - Direct expenses change      = -(A.directExp - B.directExp)
 *   - Indirect expenses change    = -(A.indirectExp - B.indirectExp)
 *   + Indirect income change      = A.indirectInc - B.indirectInc
 */

const buildSteps = (comparison) => {
    if (!comparison) return [];
    const { totals, periodA, periodB } = comparison;
    const startNet = totals.netProfit.b.amount;
    const endNet   = totals.netProfit.a.amount;

    const dSales      =  totals.sales.deltaAmount;
    const dDirectExp  = -totals.directExpenses.deltaAmount;
    const dIndirectExp= -totals.indirectExpenses.deltaAmount;
    const dIndirectInc=  totals.indirectIncome.deltaAmount;

    // Build cumulative steps
    let running = startNet;
    const steps = [];

    steps.push({
        name: `${periodB.label} Net Profit`,
        value: startNet,
        kind: 'anchor',
        cumulative: startNet,
        base: 0,
        bar: startNet
    });

    const addStep = (name, delta) => {
        const prev = running;
        running += delta;
        steps.push({
            name,
            value: delta,
            kind: delta >= 0 ? 'gain' : 'loss',
            cumulative: running,
            base: delta >= 0 ? prev : running,
            bar: Math.abs(delta)
        });
    };

    addStep('Sales change', dSales);
    addStep('Direct expenses change', dDirectExp);
    addStep('Indirect expenses change', dIndirectExp);
    addStep('Indirect income change', dIndirectInc);

    steps.push({
        name: `${periodA.label} Net Profit`,
        value: endNet,
        kind: 'anchor',
        cumulative: endNet,
        base: 0,
        bar: endNet
    });

    return steps;
};

const COLORS = {
    anchor: '#0f766e',
    gain:   '#16a34a',
    loss:   '#dc2626'
};

const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const item = payload[0].payload;
    return (
        <div className="fr-tooltip">
            <div className="fr-tooltip-label">{item.name}</div>
            <div>
                {item.kind === 'anchor'
                    ? <strong>{fmtMoney(item.value)}</strong>
                    : <strong style={{ color: item.kind === 'gain' ? COLORS.gain : COLORS.loss }}>
                        {item.value >= 0 ? '+' : ''}{fmtMoney(item.value, { currency: 'QAR' })}
                    </strong>
                }
            </div>
            {item.kind !== 'anchor' && (
                <div className="fr-tooltip-sub">Running: {fmtMoney(item.cumulative)}</div>
            )}
        </div>
    );
};

const WaterfallChart = ({ comparison }) => {
    const steps = useMemo(() => buildSteps(comparison), [comparison]);
    if (!steps.length) return null;

    return (
        <div className="fr-card">
            <div className="fr-card-header">
                <h3>Profit Bridge</h3>
                <p>How each driver moved net profit from {comparison.periodB.label} to {comparison.periodA.label}.</p>
            </div>
            <div className="fr-chart-wrap">
                <ResponsiveContainer width="100%" height={340}>
                    <BarChart data={steps} margin={{ top: 20, right: 20, left: 20, bottom: 40 }}>
                        <XAxis dataKey="name" interval={0} angle={-15} textAnchor="end" height={70} tick={{ fontSize: 11 }} />
                        <YAxis tickFormatter={fmtMoneyCompact} tick={{ fontSize: 11 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <ReferenceLine y={0} stroke="#94a3b8" />
                        <Bar dataKey="base" stackId="a" fill="transparent" />
                        <Bar dataKey="bar" stackId="a">
                            {steps.map((s, i) => (
                                <Cell key={i} fill={COLORS[s.kind]} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="fr-waterfall-legend">
                <span className="fr-legend-dot" style={{ background: COLORS.anchor }}></span> Period totals
                <span className="fr-legend-dot" style={{ background: COLORS.gain }}></span> Improves profit
                <span className="fr-legend-dot" style={{ background: COLORS.loss }}></span> Reduces profit
            </div>
        </div>
    );
};

export default WaterfallChart;
